/**
 * Async Reducer Helper - Provides non-blocking array processing for Redux reducers
 * Uses setTimeout(0) to yield control to the event loop between chunks
 */

export interface AsyncProcessOptions<T, R> {
    /** Array of items to process */
    items: T[];
    /** Function to process each item */
    processor: (item: T) => R;
    /** Number of items to process per chunk (default: 50) */
    chunkSize?: number;
    /** Callback for progress updates */
    onProgress?: (processed: number, total: number) => void;
}

/**
 * Process a large array of items into a keyed object without blocking the UI
 * Returns a Promise that resolves with the final keyed object
 */
export const processArrayToKeyedObject = async <T extends { id: string }, R>(
    options: AsyncProcessOptions<T, R>
): Promise<{ [key: string]: R }> => {
    const { items, processor, chunkSize = 25, onProgress } = options;
    const result: { [key: string]: R } = {};

    for (let i = 0; i < items.length; i += chunkSize) {
        const endIndex = Math.min(i + chunkSize, items.length);

        // Process chunk synchronously
        for (let j = i; j < endIndex; j++) {
            const item = items[j];
            if (item !== undefined) {
                result[item.id] = processor(item);
            }
        }

        // Report progress
        if (onProgress) {
            onProgress(endIndex, items.length);
        }

        // Yield control to event loop if not the last chunk
        if (endIndex < items.length) {
            await new Promise(resolve => setTimeout(resolve, 10)); // 10ms delay to ensure UI updates
        }
    }

    return result;
};

/**
 * Process a large array without blocking the UI
 * Returns a Promise that resolves with the processed array
 */
export const processArray = async <T, R>(options: AsyncProcessOptions<T, R>): Promise<R[]> => {
    const { items, processor, chunkSize = 25, onProgress } = options;
    const result: R[] = [];

    for (let i = 0; i < items.length; i += chunkSize) {
        const endIndex = Math.min(i + chunkSize, items.length);

        // Process chunk synchronously
        for (let j = i; j < endIndex; j++) {
            const item = items[j];
            if (item !== undefined) {
                result.push(processor(item));
            }
        }

        // Report progress
        if (onProgress) {
            onProgress(endIndex, items.length);
        }

        // Yield control to event loop if not the last chunk
        if (endIndex < items.length) {
            await new Promise(resolve => setTimeout(resolve, 10)); // 10ms delay to ensure UI updates
        }
    }

    return result;
};

/**
 * Determines if an array is large enough to warrant async processing
 */
export const shouldUseAsyncProcessing = (itemCount: number, threshold = 100): boolean => {
    return itemCount > threshold;
};
